import {
  Button,
  DateInput,
  DatePicker as ReactAriaDatePicker,
  Label,
  Calendar,
  Group,
} from 'react-aria-components/DatePicker';
import { Heading } from 'react-aria-components/Heading';
import { KeyboardEventHandler } from 'react';
import styled from 'styled-components';
import {
  CommonInputIconProps,
  commonInputIconStyle,
  CommonInputLabelProps,
  commonInputLabelStyle,
  StyledCommonInputProps,
  styledCommonInputStyle,
} from './Forms.styled';
import { ButtonOrLinkStyle, commonButtonStyle } from './Button';
import { toRem } from '../../../utils/helpers';
import { isSafari } from 'react-device-detect';

export const DatePickerDateInput = styled(DateInput)<{
  /**
   * We can't use flatten `StyledCommonInputProps` props directly here because of
   * "Warning: React does not recognize the `hasError` prop on a DOM element." message.
   * It seems that react-aria passes all props to the DOM element.
   */
  $styledProps: StyledCommonInputProps;
  /**
   * `DateInputProps` does not declare keyboard events, but react-aria
   * passes them to the DOM element too.
   */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}>`
  ${({ $styledProps }) => styledCommonInputStyle($styledProps)};
  display: flex;
  align-items: center;

  .react-aria-DateSegment {
    display: flex;
    align-self: center;
    padding: 1px 1px 0 1px;

    ${isSafari &&
    `
      padding-top: 2px;
    `}

    &[data-type='literal'] {
      padding: 1px 0 0 0;
    }

    &[data-placeholder] {
      color: var(--textdisabled);

      ~ .react-aria-DateSegment[data-type='literal'] {
        color: var(--textdisabled);
      }
    }

    ${({ $styledProps: { disabled, readOnly } }) =>
      !disabled &&
      !readOnly &&
      `
      &:focus {
        background-color: var(--primary);
        border: none;
        border-radius: 3px;
        outline: none;
        color: var(--white);
      }
    `}
  }
`;

export const DatePickerIcon = styled(Button)<CommonInputIconProps>`
  ${commonInputIconStyle};
  padding: 0;
  background: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export const DatePickerLabel = styled(Label)<{
  $styledProps: CommonInputLabelProps;
}>`
  ${({ $styledProps }) => commonInputLabelStyle($styledProps)};
  cursor: default;
`;

export const DatePicker = styled(ReactAriaDatePicker)`
  width: 100%;
  height: 100%;
`;

export const DatePickerGroup = styled(Group)`
  width: 100%;
  height: 100%;
`;

export const DatePickerHeaderWrapper = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 0;
  padding: 0 2px;
`;

export const DatePickerHeaderButton = styled(Button).attrs(() => ({
  draggable: false,
}))<ButtonOrLinkStyle>`
  ${commonButtonStyle}
  min-width: ${toRem(36)}rem;
  max-width: ${toRem(36)}rem;
  min-height: ${toRem(36)}rem;
  max-height: ${toRem(36)}rem;
  font-size: var(--body1);
  border-radius: var(--s4);
`;

export const DatePickerHeaderDate = styled(Heading)`
  font-size: var(--h4);
  display: flex;
  justify-content: center;
  white-space: nowrap;
  margin: 0 var(--s1);
`;

export const DatePickerCalendar = styled(Calendar)`
  font-family: inherit;
  font-size: inherit;
  background-color: var(--white);
  color: inherit;
  border: none;
  display: inline-block;
  position: relative;
  padding: var(--s3);
  border-radius: var(--s2);
  box-shadow: var(--shadow-3);

  .react-aria-CalendarGrid {
    margin-top: ${toRem(8)}rem;
  }

  .react-aria-CalendarHeaderCell,
  .react-aria-CalendarCell {
    min-width: ${toRem(36)}rem;
    max-width: ${toRem(36)}rem;
    line-height: 1;
    margin: 0;
    font-size: var(--body1);
    border-radius: 50%;
    transition: 0.2s ease background-color, 0.2s ease color;
  }

  .react-aria-CalendarHeaderCell {
    height: ${toRem(36)}rem;
  }

  .react-aria-CalendarCell {
    min-height: ${toRem(36)}rem;
    max-height: ${toRem(36)}rem;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &[data-outside-month] {
      display: none;
    }

    &:hover:not([data-disabled]):not([data-unavailable]) {
      background-color: var(--greenmuted);
    }

    &[data-selected] {
      background-color: var(--primary);
      color: var(--white);

      &:hover {
        background-color: var(--primary);
      }
    }

    &[data-disabled],
    &[data-unavailable] {
      color: var(--disableddimmed);
      cursor: not-allowed;
    }
  }
`;
